import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { AppLoginPrincipalService, AppLoginSubjectService, IdentityUserProfileService, LinkSubjectToTeamService, Pagination, TeamLoginEPService, TeamService } from 'blg-akaun-ts-lib';
import { PermissionFacade } from '../../../../../modules/permission/facades/permission.facade';
import { TeamModel, UserTeamModel } from '../../../../../models/team-permissions.model';
import { PaginationComponent } from '../../../../../utilities/pagination/pagination.component';
import { ViewColumnComponent } from '../../../../../view-column.component';
import { AppConfig } from '../../../../../visa';
import { forkJoin, iif, Observable, of, zip } from 'rxjs';
import { filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink2';
import { PermissionStates } from '../../../permission-controller/root';
import { TeamPermActions } from '../../../team-permissions-controller/actions';
import { TeamPermSelectors } from '../../../team-permissions-controller/selectors';
import { TeamPermStates } from '../../../team-permissions-controller/states';

interface LocalState {
  deactivateAdd: boolean;
  deactivateList: boolean;
}

@Component({
  selector: 'app-team-permission-listing',
  templateUrl: './team-permission-listing.component.html',
  styleUrls: ['./team-permission-listing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class TeamPermissionListingComponent extends ViewColumnComponent {

  @ViewChild(PaginationComponent, {static: false})
  private paginationComponent: PaginationComponent;

  protected readonly index = 0;
  private localState: LocalState;

  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateAdd$ = this.componentStore.select(state => state.localState.deactivateAdd);
  readonly deactivateList$ = this.componentStore.select(state => state.localState.deactivateList);

  toggleColumn$: Observable<boolean>;

  // api visa
  apiVisa = AppConfig.apiVisa;
  appletGuid = sessionStorage.getItem('appletGuid');
  // initial grid state
  gridApi;
  sideBar = 'columns';
  defaultColDef = {
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {suppressFilterButton: true},
    minWidth: 200,
    flex: 2,
    sortable: true,
    resizable: true,
    suppressCsvExport: true
  };
  columnsDefs;
  rowData = [];
  rowModelType = 'serverSide';
  serverSideStoreType = 'partial';
  searchValue: any;

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;

  currentUserGuid: string;

  constructor(
    // private appLoginPrincipalService: AppLoginPrincipalService,
    private appLoginSubjectService: AppLoginSubjectService,
    private identityUserProfileService: IdentityUserProfileService,
    // private teamService: TeamService,
    private teamService: TeamLoginEPService,
    private linkSubjectToTeamService: LinkSubjectToTeamService,
    private readonly store: Store<PermissionStates>,
    private viewColFacade: PermissionFacade,
    private readonly componentStore: ComponentStore<{localState: LocalState}>
  ) {
    super();
  }

  /**
   * Lifecycle method: called when component is first initialised.
   */
   ngOnInit(): void {
    // initial configurations for toggle column and local state
    this.toggleColumn$ = this.viewColFacade.toggleColumn$;
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState({localState: a});
    });

    // initialise column definitions
    this.columnsDefs = [
      {
        headerName: 'Team Name',
        field: 'app_mst_grp_hdr.name',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: 'Team Code',
        field: 'app_mst_grp_hdr.code',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: "No. of Members",
        field: 'num_members',
        headerClass: 'text-right',
        cellStyle: {textAlign: 'right'}
      }
    ];

    // get info about the current user
    this.getCurrentUser();

  }

  /**
   * Decides whether the data is being filtered based on the filterModel
   * provided by the AG grid request and returns what contains an object
   * that contains a function which takes in a view model and decides if
   * it contains the filter search string and a boolean whether the data is
   * currently being filtered or not.
   *
   * @param filterModel : the filter model provided by AG grid
   * @returns {by: a function, isFiltering: true if not currently filtered, false otherwise}
   */
   pageFiltering(filterModel){
    var noFilters = Object.keys(filterModel).length <= 0;
    if (noFilters) return {
      by : (viewModel) => true,
      isFiltering : noFilters
    }
    return {
      by: (viewModel: TeamModel) =>  Object
        .keys(filterModel)
        .map(col => {
          const fields = col.split(".")
          const val = fields.reduce(((acc, property) => acc ? acc[property] : null), viewModel)

          return filterModel.filterType == "number" ?
            +val === +filterModel[col].filter :
            val.toLowerCase().includes(filterModel[col].filter.toLowerCase())})
        .reduce((p, c)=> p && c),
      isFiltering: noFilters
    }
  }

  /**
   * Sorts data according to the sort model provided by AG grid and returns
   * this new data.
   *
   * @param sortModel : the sort model provided by AG grid
   * @returns the sorted data
   */
  pageSorting(sortModel){
    return (data) => {
      // return data if there is no sorting required
      if (sortModel.length <= 0) return data;
      // make a copy of data
      let newData = data.map(o => o);
      sortModel.forEach(model => {
        const col = model.colId;
        const fields = col.split(".")
        const getVal = (entity) => fields.reduce((acc, property) => acc ? acc[property] : null, entity)

        // sort the data
        newData = model.sort == 'asc' ?
          // p and c are of type VendorContainerModel
          newData.sort((p,c) => 0 - (getVal(p) > getVal(c) ? -1 : 1)) :
          newData.sort((p,c) => 0 - (getVal(p) > getVal(c) ? 1 : -1))}
      )

      return newData;
    }
  }

  /**
   * This function is called when AG grid is first initialised and initially
   * set the datasource of the grid.
   *
   * @param params : grid parameters
   */
   onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();

    const datasource = this.getDatasource();
    this.gridApi.setServerSideDatasource(datasource);

    this.store.select(TeamPermSelectors.selectUpdateTeamListing)
    .subscribe(x => {
      if (x) {
        // update grid if there have been updates
        this.gridApi.refreshServerSideStore();
        this.store.dispatch(TeamPermActions.setUpdateTeamListing({update: false}));
      }
    })

  }

  /**
   * Returns the datasource for the AG grid.
   *
   * @param conditionalCriteria : any custom criteria to search for in the API
   * @returns the datasource
   */
  getDatasource(conditionalCriteria?) {

    let offset = 0;
    let limit = this.paginationComponent.rowPerPage;

    return {
      getRows: grid => {

        // dispatch action for loading teams
        this.store.dispatch(TeamPermActions.loadTeamsInit({request: grid.request}));

        if (!conditionalCriteria) {
          conditionalCriteria = [
            {columnName: 'calcTotalRecords', operator: '=', value: 'true'}
          ]
        }

        const sortModel = grid.request.sortModel;
        const filterModel = grid.request.filterModel;
        const sortOn = this.pageSorting(sortModel);
        const filter = this.pageFiltering(filterModel);

        // only change offset and limit if no filterModel or sortModel is present
        if (Object.keys(filterModel).length == 0 && sortModel.length == 0) {
          offset = grid.request.startRow;
          limit = grid.request.endRow - offset;
        }

        const pagination = new Pagination(offset, limit, conditionalCriteria);

        this.subSink.sink = this.teamService.getByGuidCriteria(pagination, this.apiVisa, this.appletGuid.toString())
        .pipe(
          mergeMap (a => {

            const source: Observable<TeamModel>[] = [];
            a.data.forEach((b) => {
              // create a new TeamModel object
              const teamModel: TeamModel = new TeamModel();
              teamModel.app_mst_grp_hdr = b.app_mst_grp_hdr;
              teamModel.app_mst_grp_lines = b.app_mst_grp_lines;
              teamModel.app_mst_grp_exts = b.app_mst_grp_exts;

              const createdBy$ = b.app_mst_grp_hdr.created_by_subject_guid ? this.getUser(b.app_mst_grp_hdr.created_by_subject_guid.toString()) : of(null);
              const updatedBy$ = b.app_mst_grp_hdr.updated_by_subject_guid ? this.getUser(b.app_mst_grp_hdr.updated_by_subject_guid.toString()) : of(null);
              const members$ = this.getMembers(b.app_mst_grp_hdr.guid.toString());
              const result$ = zip(members$, createdBy$, updatedBy$)
              .pipe(
                map(([members, createdBy, updatedBy]) => {
                  teamModel.num_members = members.totalRecords
                  teamModel.created_by_name = createdBy ? createdBy.app_login_principal.principal_id.toString() : null;
                  teamModel.updated_by_name = updatedBy ? updatedBy.app_login_principal.principal_id.toString() : null;
                  return teamModel
                })
              )
              source.push(result$);
            })
            return iif(() => source.length > 0,
            forkJoin(source).pipe(map(x => {
              return {...a,
              data: x}
            })),
            of({...a, data: [], totalRecords: 0}))
          })
        ).subscribe(a => {
          // put data into the grid
          const data = sortOn(a.data).filter(entity => filter.by(entity));
          const totalRecords = filter.isFiltering ? a.totalRecords : data.length;
          // force pagination to go to first page if filter is applied
          if (this.paginationComponent.currentPage > this.paginationComponent.totalPage.value) { this.paginationComponent.firstPage() }

          grid.successCallback(data, totalRecords);
          this.store.dispatch(TeamPermActions.loadTeamsSuccess({totalRecords: a.totalRecords}))
        }, err => {
          grid.failCallback();
          this.store.dispatch(TeamPermActions.loadTeamsFailure({error: err.message}))
        })
      }
    }

  }

  /**
   * Return the members of the group specified in appGrpGuid.
   *
   * @param appGrpGuid : the group guid to search for
   * @returns members of this group
   */
  getMembers(appGrpGuid: string) {
    const criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
      {columnName: 'app_grp_guid', operator: '=', value: appGrpGuid}
    ];
    const pagination = new Pagination(null, null, criteria);

    return this.linkSubjectToTeamService.getByCriteria(pagination, this.apiVisa)
      .pipe()
  }

  /**
   * Return the AppLoginPrincipalContainerModel of the subject with subjectGuid
   * as sepcified in the parameter.
   *
   * @param subjectGuid : the subject guid to the name of
   * @returns an observable of type AppLoginPrincipalContainerModel which
   * contains information of the subject
   */
   getUser(subjectGuid: string) {

    // initialise new pagination
    const pagination = new Pagination();
    // add necessary criteria
    pagination.conditionalCriteria.push({
      columnName: 'guids',
      operator: '=',
      value: subjectGuid
    });

    return this.appLoginSubjectService.getByCriteria(pagination, this.apiVisa)
      .pipe(
        map((subject) => {
          subject.data[0]
        })
      )

  }

  /**
   * Gets information of the current user that is currently logged in.
   */
   getCurrentUser() {
    const currentUser$ = this.getOwnProfile();

    this.subSink.sink = currentUser$
    .subscribe((user) => {
      const newUser = <UserTeamModel>{
        // contains info about user mobile ID
        app_login_principal_mobile: null,
        // contains info about user email ID
        app_login_principal_email: null,
        // links to groups
        link_subject_to_grps: user.teams.map(x => x.link_subject_to_grp)
      }

      user.profile['appLoginPrincipalList'].forEach(x => {
        if (x.principal_type == 'MOBILE_USERNAME') {
          newUser.app_login_principal_mobile = x
        } else {
          newUser.app_login_principal_email = x
        }
      })

      // save in store
      this.store.dispatch(TeamPermActions.setCurrentUser({currentUser: newUser}));
    })
  }

  /**
   * Returns an object containing the current info. of the current user in the profile
   * field and the teams that the current user is part of in the teams field.
   *
   * @returns an observable containing an object
   * {profile: AppLoginContainerModelUpdate, teams: LinkSubjectContainerModel[]}
   */
  getOwnProfile() {
    return this.identityUserProfileService.get(this.apiVisa)
    .pipe(
      map(x => {
        // x is actually of type AppLoginContainerModelUpdate
        this.currentUserGuid = x.data['appLoginSubject']['guid'].toString();
        return x.data}
        ),
      mergeMap(x => {
        const userTeam$ = this.getUserTeams(this.currentUserGuid);
        return forkJoin(userTeam$)
        .pipe(map(([teams]) => {return {profile: x, teams: teams}}))
      }),
      map(x => x)
    )
  }

  /**
   * Return the links between the group and the subject that this subject is
   * part of.
   *
   * @param subjectGuid : the subject guid
   * @returns an observable contianing the groups this subject is part of
   */
  getUserTeams(subjectGuid: string) {

    const criteria = [{
      columnName: 'app_subject_guid',
      operator: '=',
      value: subjectGuid
    }];
    const pagination = new Pagination(null, null, criteria);

    return this.linkSubjectToTeamService.getByCriteria(pagination, this.apiVisa)
    .pipe(
      map((subject) => subject.data)
    )

  }

  /**
   * This function is called every time the user clicks on the toggle column
   * button. It wil trigger the screen to show either one or two columns at a
   * time.
   *
   * @param e : event
   */
   onToggle(e: boolean) {
    this.viewColFacade.toggleColumn(e);
  }

  /**
   * This function is called every time the user clicks on a row in the
   * AG grid.
   *
   * @param entity : the entity on the AG grid that was clicked on
   */
   onRowClicked(entity) {

    // dispatch ngrx actions
    this.store.dispatch(TeamPermActions.selectTeam({team: entity}));
    this.store.dispatch(TeamPermActions.selectGuid({guid: entity.app_mst_grp_hdr.guid}))

    // deactivate add and list in this local state
    if (!this.localState.deactivateList) {
      this.viewColFacade.updateInstance(this.index,
        {
          ...this.localState,
          deactivateAdd: true,
          deactivateList: true
        });
      // trigger the next view column
      this.viewColFacade.onNext(1);
    }
  }

  /**
   * Set datasource on the AG grid to empty.
   */
   clear() {
    const dataSource = {
      getRows: grid => {
        grid.successCallback([], 0)
      }
    };

    this.gridApi.setServerSideDatasource(dataSource);
  }

  /**
   * This function is called every time the user searches something in the
   * search bar.
   *
   * @param e : the search model
   */
   /**
   * Runs the search bar's keyword against the listing's own endpoint.
   *
   * Replaces the raw SELECT that went through SubQueryService:
   *
   *   FROM app_mst_grp_hdr hdr
   *   WHERE (hdr.code ILIKE '%x%' OR hdr.name ILIKE '%x%')
   *     AND hdr.status != 'DELETED'
   *
   * GET teams/applet-login-ep/{applet}/query -> TeamUow ->
   * generateWhereClauseByCriteria applies `search_word` as exactly
   * `code ILIKE ... OR name ILIKE ...` and defaults to `status != 'DELETED'`,
   * so this is a like-for-like replacement.
   */
  onSearch() {
    const datasource = this.getDatasource(this.buildSearchCriteria());
    this.gridApi.setServerSideDatasource(datasource);
  }

  /**
   * Criteria for the current keyword, or undefined when the box is empty so the
   * listing falls back to its default criteria.
   */
  buildSearchCriteria() {
    const keyword = (this.searchValue || '').toString().trim();
    if (!keyword) {
      return undefined;
    }
    return [
      { columnName: 'calcTotalRecords', operator: '=', value: 'true' },
      { columnName: 'search_word', operator: '=', value: keyword }
    ];
  }

  /**
   * Called when the user clicks on the clear button in the search bar.
   * Will reset the datasource and search value to the default.
   */
  reset() {
    this.searchValue = '';
    const datasource = this.getDatasource();
    this.gridApi.setServerSideDatasource(datasource);
  }

  /**
   * Lifecycle method: Called when component is destroyed.
   */
   ngOnDestroy() {
    this.subSink.unsubscribe();
  }

}
